/**
 * Per-ship insurance on the Career side (M6 spec §4; plan P21): optional per ship once the M2 lease is owned (the
 * lease `ownership` effect unlocks it, and founding with it). An uninsured loss is total; the M2 fee formula
 * (`death.ts rebuildFee`) applies per ship, reading the flown ship's insurance here.
 *
 * - The Career's own (lease) ship keeps M2's `lease.insured` (Lease Office premium).
 * - A hangar ship's flag is its `OwnedShip.insured`, mirrored from the worker's `setInsurance` (the worker books the
 *   premiums per faction day; the Career only needs the flag for the fee and the UI).
 *
 * Pure: the input career is never changed.
 */
import { fail, ok, type CmdResult } from '../career/result'
import type { Career } from '../career/types'
import { OWN_SHIP_ID, flownEntry, ownedShip, updateOwnedShip } from './hangar'

/** The ownership effect has happened: per-ship insurance and founding are open (M6 spec §4, §7.1). */
export function ownershipUnlocked(career: Career): boolean {
  return career.lease.owned
}

/** Whether `fleetId` is insured (`OWN_SHIP_ID` = the lease ship). Insurance never counts before ownership. */
export function shipInsured(career: Career, fleetId: string): boolean {
  if (!career.lease.owned) return false
  if (fleetId === OWN_SHIP_ID) return career.lease.insured
  return ownedShip(career, fleetId)?.insured ?? false
}

/** Whether the ship the player flies now is insured (the death fee reads this). */
export function flownShipInsured(career: Career): boolean {
  if (!career.lease.owned) return false
  const entry = flownEntry(career)
  return entry === null ? career.lease.insured : entry.insured
}

/** Sets a hangar ship's insurance flag. Refuses before ownership, for the lease ship and for an unknown ship. */
export function setShipInsured(career: Career, fleetId: string, insured: boolean): CmdResult<Career> {
  if (fleetId === OWN_SHIP_ID) return fail('serviceUnavailable', "The lease ship's insurance is set at the Lease Office.")
  if (insured && !ownershipUnlocked(career)) return fail('serviceUnavailable', 'Per-ship insurance opens once the lease is paid off.')
  const entry = ownedShip(career, fleetId)
  if (entry === null) return fail('invalidAmount', `Ship ${fleetId} is not in the hangar.`)
  if (entry.insured === insured) return ok(career)
  return ok(updateOwnedShip(career, fleetId, (e) => ({ ...e, insured })))
}
